import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import PulsingLoader from '@/components/PulsingLoader';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ImageVariation } from '@/pages/CoverImagesGenerator/components/VariationTwo';
import GeneratedImages from '@/pages/CoverImagesGenerator/components/VariationTwo/GeneratedImages';
import VariationsStyles from '@/pages/CoverImagesGenerator/components/VariationTwo/VariationsStyles';
import {
  CoverImagesAiContentResource,
  CoverImagesProjectResource,
} from '@/types/resources';
import { api } from '@/utils/api';

import AiGeneratedImages from './AiGeneratedImages';
import AiGeneratedTexts from './AiGeneratedTexts';

const Box = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.white};
  margin-bottom: 48px;
`;

const BoxHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  padding: 28px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BoxInner = styled.div`
  text-align: center;
  padding: 32px;
  padding-top: 24px;
`;

const Heading = styled(Text)`
  margin: 8px 0;
`;

const Description = styled(Text)`
  line-height: 22px;
  max-width: 268px;
  margin: auto;
`;

const GenerateButton = styled(Button)``;

interface Props {
  project: CoverImagesProjectResource;
}

const AiGeneratedCovers: FC<Props> = ({ project }) => {
  const [isPosting, setIsPosting] = useState(false);
  const [images, setImages] = useState<string[]>();
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [showContent, setShowContent] = useState(true);

  const queryClient = useQueryClient();

  const { id } = useParams();

  const alerts = useAlerts();

  const { data: aiContent, isError } = useQuery<CoverImagesAiContentResource>(
    ['cover_images_project_ai_content', { id }],
    () =>
      api.get<never, CoverImagesAiContentResource>(
        '/v1/cover-images/cover-images-project-ai-content',
        {
          params: {
            project_id: id,
          },
        },
      ),
    {
      refetchIntervalInBackground: true,
      refetchInterval: 20000,
    },
  );

  const [selectedTexts, setSelectedTexts] = useState(
    aiContent?.aiTexts.map((aiText) => aiText.id) || [],
  );

  const [selectedImages, setSelectedImages] = useState(
    aiContent?.images?.map((image) => image.id) || [],
  );

  useEffect(() => {
    if (aiContent) {
      setSelectedTexts(aiContent.aiTexts.map((aiText) => aiText.id));
      setSelectedImages(aiContent.images?.map((image) => image.id) || []);
    }
  }, [aiContent]);

  const onAiGenerate = async () => {
    setIsPosting(true);
    try {
      await api.post<any, { projectId: number }>(
        '/v1/cover-images/generate-ai-content',
        {
          project_id: id,
          article_id: project.articleId,
          is_open_list: project.isOpenList,
        },
      );

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      await queryClient.invalidateQueries(
        ['cover_images_project_ai_content', { id }],
        {
          exact: true,
        },
      );

      alerts.success('Generating in progress');
    } catch (err) {
      alerts.error((err as any)?.response?.data?.message);
    }
    setIsPosting(false);
  };

  const generateVariations = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!aiContent) return;

    const newVariations = selectedImages.flatMap((selectedImageId) =>
      selectedTexts.map((selectedTextId) => {
        const image = aiContent.aiImages.find(
          (aiImage) => aiImage.id === selectedImageId,
        )!.url;
        const leftImageSrc = image;
        return {
          leftImageSrc,
          leftWatermarkTag: 'none',
          leftWatermarkPosition: 'bottom',
          rightText: aiContent.aiTexts.find(
            (aiText) => aiText.id === selectedTextId,
          )!.text,
          quotes: true,
          brakes: false,
          divider: 'none',
        };
      }),
    );

    setVariations(newVariations);
  };

  const onReset = () => {
    setImages([]);
  };

  return (
    <>
      <Box>
        <BoxHeader>
          <Text $type="button">
            Ai Content {!project.hasAiContent && 'not yet prepared'}
          </Text>
          {!!project.hasAiContent ? (
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          ) : (
            <GenerateButton
              loading={isPosting}
              disabled={isPosting}
              color="colorized"
              onClick={onAiGenerate}
            >
              Generate Content
            </GenerateButton>
          )}
        </BoxHeader>
        {isError && 'error'}
        {showContent && !!project.hasAiContent && (
          <BoxInner>
            <AiGeneratedTexts
              project={project}
              aiTexts={aiContent?.aiTexts}
              selectedTexts={selectedTexts}
              setSelectedTexts={setSelectedTexts}
            />
            {!!aiContent?.aiImages.length ? (
              <>
                <AiGeneratedImages
                  project={project}
                  images={aiContent.images}
                  selectedImages={selectedImages}
                  setSelectedImages={setSelectedImages}
                />
                <Button onClick={generateVariations}>Get Cover Images</Button>
              </>
            ) : (
              <div>
                <PulsingLoader />
                <Heading $type="button">Preparing content ...</Heading>
                <Description $color="gray" $type="captionSemibold">
                  Please be patient, this action may take a little longer in
                  some cases
                </Description>
              </div>
            )}
          </BoxInner>
        )}
      </Box>
      <VariationsStyles />
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={true}
        onReset={onReset}
        isAiGenerated={true}
      />
    </>
  );
};

export default AiGeneratedCovers;
